import * as React from 'react';
import { Box, alpha } from '@mui/system';
import { Button, Chip, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useQueryClient } from '@tanstack/react-query';
import { AgentActionRequest, AgentInterrupt, ReviewDecision } from 'bloggers-backend/lib/aiagents/domain';
import { AppContext } from '../../../AppContext';
import { ArgFieldLabel, ArgFieldValue, approvalArgRenderer } from './approvalArgRenderers';

type DecisionType = 'approve' | 'reject';

// Shown when the latest turn paused before approval-required tool calls. The user reviews every
// pending action, then approves or rejects the whole batch in one decision. Approval is all-or-
// nothing on purpose: a mixed approve/reject resume desyncs Gemini's function-call/response parity
// (the middleware drops approved calls but their content blocks remain), so we only ever send a
// uniform decision. The decision resumes the turn via the same aiagents.sendMessage event the input
// box uses.
export function ApprovalRequest({
  chatId,
  interrupt,
  chatsCacheName,
}: {
  chatId: string;
  interrupt: AgentInterrupt;
  chatsCacheName?: string;
}) {
  const { client, notificationsService } = React.useContext(AppContext);
  const queryClient = useQueryClient();
  const theme = useTheme();
  const [loading, setLoading] = React.useState(false);

  const requests = interrupt.actionRequests;
  const multiple = requests.length > 1;
  const canApprove = requests.every((request) =>
    (request.allowedDecisions ?? ['approve', 'reject']).includes('approve'),
  );
  const canReject = requests.every((request) =>
    (request.allowedDecisions ?? ['approve', 'reject']).includes('reject'),
  );

  async function respond(type: DecisionType) {
    if (loading) {
      return;
    }

    // One uniform decision per pending action, in the order the agent proposed them.
    const decisions: ReviewDecision[] = requests.map(() =>
      type === 'approve' ? { type: 'approve' } : { type: 'reject' },
    );

    setLoading(true);
    const result = await client<any, any>({
      event: 'aiagents.sendMessage',
      data: { chatId, decisions },
    });
    setLoading(false);

    if (result.success) {
      if (chatsCacheName) {
        await queryClient.invalidateQueries([chatsCacheName]);
      }
    } else {
      notificationsService.notify({
        severity: 'error',
        message: `Error: ${JSON.stringify(result.errors)}`,
      });
    }
  }

  return (
    <Box sx={{ display: 'flex', mb: 2 }}>
      <Box
        sx={{
          width: '80%',
          p: 1.5,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.warning.main, 0.5)}`,
          bgcolor: alpha(theme.palette.warning.main, 0.06),
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Chip
          size="small"
          color="warning"
          icon={<WarningAmberIcon />}
          label={`Approval required · ${requests.length} action${multiple ? 's' : ''}`}
          sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
        />

        {requests.map((request, index) => (
          <ApprovalAction key={index} request={request} />
        ))}

        {loading ? (
          <CircularProgress size={20} sx={{ mt: 0.5 }} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {canApprove && (
              <Button size="small" variant="contained" color="success" onClick={() => void respond('approve')}>
                {multiple ? 'Approve all' : 'Approve'}
              </Button>
            )}
            {canReject && (
              <Button size="small" variant="outlined" color="error" onClick={() => void respond('reject')}>
                {multiple ? 'Reject all' : 'Reject'}
              </Button>
            )}
            {multiple && (
              <Typography variant="caption" color="text.secondary">
                Applies to all {requests.length} actions.
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// Values are shown verbatim (pre-wrap, no markdown) so the reviewer sees exactly the text the tool
// will receive — a single newline in an email body must stay a single line break.
function formatArgValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
}

// A single pending tool call inside an approval request, rendered read-only for review: the tool
// name plus a key/value list of every arg. Args are sorted by key because the model emits them in
// an arbitrary order, which would otherwise shuffle the fields from one approval to the next.
// Args a specific tool renders specially (an opaque id resolved to the entity it points at) go
// through approvalArgRenderer; everything else falls back to formatArgValue.
// The approve/reject decision is taken for the whole batch by ApprovalRequest, not per action.
function ApprovalAction({ request }: { request: AgentActionRequest }) {
  const args = request.args && typeof request.args === 'object' ? (request.args as Record<string, unknown>) : {};
  const entries = Object.entries(args).sort(([a], [b]) => a.localeCompare(b));

  return (
    <Box
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 1.5,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderBottom: (theme) => (entries.length > 0 ? `1px solid ${theme.palette.divider}` : 'none'),
          bgcolor: 'grey.50',
        }}
      >
        <Chip
          size="small"
          variant="outlined"
          label={request.name}
          sx={{ fontFamily: 'monospace', fontSize: '0.7rem', height: 22 }}
        />
      </Box>

      {entries.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(72px, max-content) 1fr',
            columnGap: 2,
            rowGap: 1.25,
            alignItems: 'baseline',
            px: 1.5,
            py: 1.25,
          }}
        >
          {entries.map(([key, value]) => {
            const renderValue = approvalArgRenderer(request.name, key);

            return (
              <React.Fragment key={key}>
                <ArgFieldLabel>{key}</ArgFieldLabel>
                {renderValue ? renderValue(value) : <ArgFieldValue>{formatArgValue(value)}</ArgFieldValue>}
              </React.Fragment>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
